/// <reference lib="dom" />
import type { ChartLayer } from "../core/chart.ts";

export interface BarDatum {
  x0: number;
  x1: number;
  value: number;
  color?: string;
  label?: string;
}

export interface BarsLayerOpts {
  id?: string;
  gap?: number;
  barWidth?: number;
  opacity?: number;
  band?: boolean;
  labels?: boolean;
  color?: string;
}

export interface BarsLayer extends ChartLayer {
  set(bars: BarDatum[]): void;
}

export function BarsLayer(opts: BarsLayerOpts = {}): BarsLayer {
  let bars: BarDatum[] = [];
  const gap = opts.gap ?? 1;
  return {
    id: opts.id ?? "bars",
    clip: true,
    set(next) {
      bars = next;
    },
    render(root, ctx) {
      const { x, y, theme } = ctx;
      const { inner } = ctx.frame;
      const base = Math.min(inner.bottom, y(Math.max(0, y.domain()[0])));
      const left = (b: BarDatum) =>
        opts.barWidth === undefined
          ? Math.min(x(b.x0), x(b.x1))
          : x(b.x0) - opts.barWidth / 2 - gap / 2;
      const width = (b: BarDatum) =>
        opts.barWidth ?? Math.max(0.5, Math.abs(x(b.x1) - x(b.x0)) - gap);
      root.selectAll<SVGRectElement, BarDatum>("rect")
        .data(bars)
        .join((enter) => enter.append("rect").call((r) => r.append("title")))
        .attr("x", (b) => left(b) + gap / 2)
        .attr("width", width)
        .attr("y", (b) => Math.min(base, y(b.value)))
        .attr(
          "height",
          (b) => Math.max(opts.barWidth === undefined ? 0 : 1, Math.abs(base - y(b.value))),
        )
        .attr("fill", (b) => b.color ?? opts.color ?? theme.series)
        .attr("fill-opacity", opts.opacity ?? (opts.band ? theme.bandOpacity : 1))
        .select("title")
        .text((b) => `${b.label ? `${b.label}: ` : ""}${+b.value.toFixed(1)}`);
      root.selectAll<SVGTextElement, BarDatum>("text.bar-label")
        .data(opts.labels ? bars.filter((b) => b.label) : [])
        .join("text")
        .attr("class", "bar-label")
        .attr("x", (b) => left(b) + width(b) / 2 + gap / 2)
        .attr("y", inner.top + theme.fontSize + 2)
        .attr("text-anchor", "middle")
        .attr("font-size", theme.fontSize)
        .attr("fill", theme.text)
        .style("pointer-events", "none")
        .attr("display", (b) => (width(b) < b.label!.length * theme.fontSize * 0.6 ? "none" : null))
        .text((b) => b.label!);
    },
  };
}
